// © Microsoft Corporation. All rights reserved.
import { __decorate } from "tslib";
import { TreeView } from "@microsoft/fast-foundation";
import { attr, observable } from "@microsoft/fast-element";
export class HamburgerMenu extends TreeView {
    constructor() {
        super(...arguments);
        /** Flag to indicate if menu should be shown */
        this.showMenu = false;
        /**
         * Click listener to close menu if user clicks outside the menu
         * @param event - click event
         */
        this.onOverlayClick = (event) => {
            const targetClass = event.target.getAttribute("class");
            if (targetClass && targetClass.includes("overlay")) {
                this.closeMenu();
            }
        };
    }
    /**
     * Connected callback. When component is inserted into the DOM, add proper event listeners.
     */
    connectedCallback() {
        super.connectedCallback();
        this.menuOverlay.addEventListener("click", this.onOverlayClick);
    }
    /**
     * Disconnected callback. When component is removed from the DOM, remove event listeners as part of clean up.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.menuOverlay.removeEventListener("click", this.onOverlayClick);
    }
    /**
     * Opens the hamburger menu
     */
    openMenu() {
        this.showMenu = true;
    }
    /**
     * Closes the hamburger menu
     */
    closeMenu() {
        this.showMenu = false;
    }
    /**
     * Updates the currently selected item
     * @param oldValue - previously selected nav id
     * @param newValue - new selected nav id
     */
    selectedNavIdChanged(oldValue, newValue) {
        if (!newValue ||
            oldValue === newValue ||
            !this.slottedTreeItems ||
            !this.slottedTreeItems.length) {
            return;
        }
        // Check the slotted tree items and their children to find the currently selected item
        let newSelectedItem;
        for (const treeItem of this.slottedTreeItems) {
            if (treeItem.id === newValue) {
                newSelectedItem = treeItem;
                break;
            }
            if (!treeItem.childItems ||
                !treeItem.childItems.length) {
                continue;
            }
            newSelectedItem = treeItem.childItems.find(child => child.id === newValue);
            if (newSelectedItem) {
                break;
            }
        }
        if (!newSelectedItem || newSelectedItem === this.currentSelected) {
            return;
        }
        if (this.currentSelected) {
            this.currentSelected.removeAttribute("selected");
        }
        this.currentSelected = newSelectedItem;
        this.currentSelected.setAttribute("selected", "true");
    }
    /**
     * Closes menu if the new selected menu item is different from the previous menu item
     * @param oldValue - previously selected menu item
     * @param newValue - currently selected menu item
     */
    currentSelectedChanged(oldValue, newValue) {
        if (!newValue) {
            return;
        }
        if (oldValue !== newValue) {
            this.$emit("change", { menuItem: newValue });
            this.closeMenu();
        }
    }
}
__decorate([
    attr({ attribute: "menu-button-tooltip" })
], HamburgerMenu.prototype, "menuButtonTooltip", void 0);
__decorate([
    attr({ attribute: "close-button-tooltip" })
], HamburgerMenu.prototype, "closeButtonTooltip", void 0);
__decorate([
    attr({ attribute: "use-mobile-styles", mode: "boolean" })
], HamburgerMenu.prototype, "useMobileStyles", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "selectedNavId", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "showMenu", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "currentSelected", void 0);
